import React, { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, Shirt, Camera } from 'lucide-react';

/**
 * BeforeAfterSection
 * Shows 3 states: Original Photo → AI Artwork → Final Product
 * Props:
 *   beforeImage  - original photo src
 *   afterImage   - AI artwork src
 *   finalImage   - product mockup src (optional, falls back to afterImage)
 *   title        - section title (optional)
 *   subtitle     - section subtitle (optional)
 */

const steps = [
  {
    key: 'before',
    label: 'Original Photo',
    sublabel: 'You send us this',
    icon: Camera,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
    badge: 'Step 1',
    badgeBg: 'bg-gray-800',
  },
  {
    key: 'after',
    label: 'AI Artwork',
    sublabel: 'We transform it',
    icon: Sparkles,
    iconBg: 'bg-[hsl(350,70%,65%)]/10',
    iconColor: 'text-[hsl(350,70%,55%)]',
    badge: 'Step 2',
    badgeBg: 'bg-gradient-to-r from-[hsl(350,70%,55%)] to-[hsl(20,90%,60%)]',
  },
  {
    key: 'final',
    label: 'Final Product',
    sublabel: 'You wear this!',
    icon: Shirt,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    badge: 'Step 3',
    badgeBg: 'bg-green-600',
  },
];

const BeforeAfterSection = ({
  beforeImage,
  afterImage,
  finalImage,
  title = 'See the Transformation',
  subtitle = 'From your photo to a wearable masterpiece — powered by AI',
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const touchStartX = useRef(null);
  const images = [beforeImage, afterImage, finalImage || afterImage];

  const goNext = useCallback(() => {
    setActiveStep((s) => Math.min(s + 1, 2));
  }, []);

  const goPrev = useCallback(() => {
    setActiveStep((s) => Math.max(s - 1, 0));
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  const step = steps[activeStep];
  const Icon = step.icon;

  return (
    <section className="py-16 bg-gradient-to-b from-white to-[hsl(var(--cream))]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            AI Transformation
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{title}</h2>
          <p className="text-gray-600 max-w-xl mx-auto">{subtitle}</p>
        </div>

        {/* Step tabs */}
        <div className="flex justify-center gap-2 mb-8">
          {steps.map((s, i) => {
            const SIcon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setActiveStep(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  i === activeStep
                    ? 'bg-white shadow-md text-gray-900 scale-105'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${s.badgeBg}`}
                >
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Main image viewer */}
        <div className="relative">
          <div
            className="relative rounded-3xl overflow-hidden shadow-soft-lg bg-gray-50 cursor-grab select-none"
            style={{ aspectRatio: '4/3', maxHeight: '480px' }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={steps[i].label}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                  i === activeStep ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`}
              />
            ))}

            {/* Overlay label */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span
                className={`px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg ${step.badgeBg}`}
              >
                {step.badge}
              </span>
              <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-gray-800 text-xs font-medium shadow">
                {step.label}
              </span>
            </div>

            {/* AI badge on step 2 */}
            {activeStep === 1 && (
              <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-lg">
                <Sparkles className="w-4 h-4 text-[hsl(350,70%,55%)]" />
                <span className="text-xs font-semibold text-gray-800">AI Generated</span>
              </div>
            )}

            {/* Navigation arrows */}
            {activeStep > 0 && (
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
            )}
            {activeStep < 2 && (
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            )}
          </div>

          {/* Step progress dots */}
          <div className="flex justify-center gap-2 mt-4">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activeStep
                    ? 'w-8 h-2 bg-[hsl(350,70%,65%)]'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step description */}
        <div className="mt-6 text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${step.iconBg}`}>
            <Icon className={`w-4 h-4 ${step.iconColor}`} />
            <span className={`text-sm font-medium ${step.iconColor}`}>{step.sublabel}</span>
          </div>
        </div>

        {/* Swipe hint (mobile only) */}
        <p className="text-center text-xs text-gray-400 mt-3 md:hidden">
          ← Swipe to see next step →
        </p>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
